import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { AdSlotKey } from "@gooner/shared";
import { publicSettings } from "../../lib/publicSettings";
import { loadJuicyAds, placeLateZone, pushZone } from "../../lib/juicyads";
import { useSession } from "../../lib/session";
import { useGoldGate } from "../../lib/gold";
import { useShopPromo } from "../../lib/shopOpen";
import { ShopPromoCard } from "./ShopPromoCard";
import { HOW_TO_LINK } from "../../components/siteLinks";
import { GoldChip } from "../ui/Gold";
import { GoldDialog, NO_ADS_PERK } from "./GoldDialog";
import { COMPACT_NO_ADS_SUB, adOutcome, adSlotGate, houseCardKind, houseCardLayout, takesShopTurn } from "../lib/adHouseCard";
import { adRenderedOutcome, adSlotRenderedProps, createFireOnce, type FireOnce } from "../lib/adSlotEvent";
import { track } from "../../analytics";

/**
 * One display-ad placement.
 *
 * Where ads may appear is a product decision, not a styling one, so it is worth
 * stating the rule the placements follow: ads go on the surfaces where a visitor is
 * choosing what to watch (/browse, the footer of the catalog pages) and never on the
 * surfaces where they are watching. Nothing under display/ mounts this component, and
 * nothing should — a wall TV carries no advertising.
 *
 * THE COMMUNITY WALL DOES NOT BREAK THAT RULE, and the distinction is worth keeping
 * straight now that it sells promotional squares. This component is a third-party ad
 * NETWORK: an iframe we do not control, serving whoever paid the network. A wall square
 * is media the operator sold directly, vetted like any other clip, rendered by our own
 * player. The invariant here — no network iframe under display/ — stands unchanged; what
 * a person may put on a square they bought is a different question with a different
 * answer (2026-08-29).
 *
 * Three things this component guarantees, each of which has bitten sites that skipped it:
 *
 *  1. SAFE MODE. The blur rule in index.css matches `:is(img, video)`. An ad renders in
 *     an <iframe>, which that selector does not reach — so with safe mode on, an
 *     unblurred adult creative would sit beside deliberately blurred clips. Here the
 *     slot degrades to an inert grey box and never contacts the network, so there is
 *     no creative to leak and no impression counted for a view nobody can see.
 *     Fails closed: it renders live only when `data-safe-mode` is exactly "0", so a
 *     failed /api/safe-mode.js hides ads rather than revealing them.
 *
 *  2. NO LAYOUT SHIFT. The box is reserved at full height before the fill arrives.
 *     Without that the grid jumps when the iframe lands, which is most of what makes
 *     ads feel cheap.
 *
 *  3. NO EMPTY BOX. A blocked script or an unfilled zone leaves the reserved box
 *     permanently empty — a labelled hole in the page, which looks worse than no ad at
 *     all. Both cases end with the box replaced by a small HOUSE CARD of our own (the
 *     Premium pitch, or the guide while Premium is off sale — lib/adHouseCard), sized
 *     to its content rather than the unit (2026-10-21; it used to collapse to nothing,
 *     which on Browse left the grid's reserved cell as an empty band). No-fill is
 *     normal, not an error: a zone with no advertiser bidding for the visitor's geo
 *     simply returns nothing. A slot that got an iframe is filled and is never swapped,
 *     whatever the creative inside it turns out to be.
 *
 *  4. NONE FOR PREMIUM (2026-09-09). The one perk every visitor understands on sight,
 *     and it was absent — a member paid and still saw the network's creatives. `AdSlot`
 *     is a wrapper that mounts the real slot only for a non-member, and only once the
 *     session has answered: a member's page then makes no settings fetch, no script load
 *     and no ad request at all. A wrapper rather than an early return inside the slot,
 *     because a component's effects run whatever it rendered — a `return null` above
 *     the effect that loads jads.js would still have loaded jads.js.
 */

/**
 * How long to let a pushed zone fill before giving up on it.
 *
 * Deliberately generous, because fill is intermittent by design: JuicyAds
 * frequency-caps a zone per visitor, so consecutive loads legitimately alternate
 * between filled and empty, and a fill that is coming can arrive seconds after the
 * ad request. The cost of waiting is a blank box — already reserved, so it shifts
 * nothing — while the cost of being impatient is unmounting the <ins> that a
 * creative was about to land in, which throws the impression away for good.
 */
const FILL_TIMEOUT_MS = 10_000;

interface AdSize {
  width: number;
  height: number;
}

/** The tag JuicyAds looks for. Built by hand because it is injected as raw HTML; the
 *  zone is re-checked here rather than trusted from the API, so a settings file edited
 *  by hand on the server cannot put anything but digits into the markup. */
function insMarkup(zone: string, size: AdSize): string {
  if (!/^[0-9]{1,16}$/.test(zone)) return "";
  return `<ins id="${zone}" data-width="${size.width}" data-height="${size.height}" style="display:block;width:${size.width}px;height:${size.height}px"></ins>`;
}

type SlotState =
  | { kind: "unknown" }
  | { kind: "off" }
  | { kind: "placeholder" }
  | { kind: "live"; zone: string }
  | { kind: "house" }
  /** The shop's turn: a Featured product instead of an ad, which is never even requested. */
  | { kind: "shop" };

interface AdSlotProps {
  slot: AdSlotKey;
  size: AdSize;
  className?: string;
  /** Told when the slot turns into the house card, so a parent holding a reserved
   *  cell for the unit (Browse's in-feed cell) can let it shrink to the card. */
  onHouse?: () => void;
  /** The slot sits in a cell bigger than its unit (Browse's two-tile in-feed cell): a shop
   *  card fills the whole cell, like a tile, instead of sitting in the middle of it. An ad
   *  keeps its own size either way. */
  fill?: boolean;
}

export function AdSlot(props: AdSlotProps) {
  const { user, ready } = useSession();
  // Held until the token check has answered, or a member's first paint would mount the
  // slot and start an ad they are about to be spared. Judged on the session alone —
  // the same `goldUntil > now` the cap nudge and the Gold dialog read — and not on
  // useGoldGate, which reports "not gold" until the SETTINGS land too, a second flash
  // this wrapper has no reason to inherit.
  if (!ready) return null;
  // A member never meets the ad network: their slot shows one of our own shop's products,
  // and nothing on this path loads the network's script or pushes it a zone.
  if ((user?.goldUntil ?? 0) > Date.now()) return <MemberShopSlot {...props} />;
  return <AdSlotInner {...props} />;
}

/**
 * A member's slot: the shop promo, where a non-member's slot would have been live, and
 * nothing anywhere else (ads off, no zone, safe mode). It goes through the same gate as an
 * ad so switching ads off in the admin panel empties members' slots too.
 */
function MemberShopSlot({ slot, size, className, onHouse, fill }: AdSlotProps) {
  const [live, setLive] = useState<boolean | null>(null);
  const promo = useShopPromo();
  useEffect(() => {
    let cancelled = false;
    void publicSettings().then(({ ads }) => {
      if (!cancelled) setLive(adSlotGate(ads, slot, document.documentElement.dataset.safeMode).kind === "live");
    });
    return () => {
      cancelled = true;
    };
  }, [slot]);
  const show = live === true && promo.ready && !!promo.product;
  const settled = live === false || (live === true && promo.ready);
  // Browse holds a cell for its in-feed unit; this tells it the cell can take the card's own
  // height, or none at all when there is nothing to show.
  useEffect(() => {
    if (settled) onHouse?.();
  }, [settled, onHouse]);
  if (!show) return null;
  return <HouseCard width={size.width} height={size.height} className={className} shopTurn member fill={fill} />;
}

function AdSlotInner({ slot, size, className, onHouse, fill }: AdSlotProps) {
  const [state, setState] = useState<SlotState>({ kind: "unknown" });
  // Half the live slots go to the shop before any ad is asked for (lib/adHouseCard). Only a
  // slot that would have shown an ad takes a turn: off, safe mode and members never do.
  const [shopTurn] = useState(() => takesShopTurn(Math.random()));
  const promo = useShopPromo();
  const [filled, setFilled] = useState(false);
  const pushed = useRef(false);
  const hostRef = useRef<HTMLDivElement | null>(null);
  // ad_slot_rendered (lib/adSlotEvent): one event per mount, once the outcome resolves.
  // The latch lives in a ref so StrictMode's dev remount reuses it and cannot fire twice,
  // and it is unmounted on the way out so a timer or promise that settles after the slot
  // has gone reports nothing. Only a live slot ever gets here with a zone: off, safe mode
  // and members never push one, so they never report.
  const latch = useRef<FireOnce | null>(null);
  latch.current ??= createFireOnce();
  const liveZone = useRef<string | null>(null);
  const houseCause = useRef<{ scriptFailed: boolean; deadlinePassed: boolean } | null>(null);
  const report = (e: { iframe: boolean; scriptFailed: boolean; deadlinePassed: boolean; houseCard: boolean }) => {
    const zone = liveZone.current;
    const r = adRenderedOutcome(e);
    // Closed props (allowlist + types): null means something did not fit, and nothing is sent.
    const props = zone && r ? adSlotRenderedProps(slot, zone, r, window.innerWidth) : null;
    if (!props) return;
    latch.current?.fire(() => track("ad_slot_rendered", props));
  };

  useEffect(() => {
    const l = latch.current;
    l?.mount();
    return () => l?.unmount();
  }, []);

  useEffect(() => {
    let cancelled = false;
    // The page-wide settings read. Unreadable settings must not mean "show ads": the
    // fallback is DEFAULT_SETTINGS, whose ads block is off, so a slot that cannot
    // confirm it is enabled stays off.
    void publicSettings().then(({ ads }) => {
      if (cancelled) return;
      // Safe mode is decided after the enabled check so a site with ads switched off
      // shows no grey boxes either — the placeholder means "an ad belongs here", not
      // "ads exist". Off and safe mode never reach the house card.
      setState(adSlotGate(ads, slot, document.documentElement.dataset.safeMode));
    });
    return () => {
      cancelled = true;
    };
  }, [slot]);

  useEffect(() => {
    if (state.kind !== "live" || pushed.current) return;
    if (shopTurn) {
      // Wait for the shop's answer: with nothing to promote, the slot falls back to the ad.
      if (!promo.ready) return;
      if (promo.product) {
        setState({ kind: "shop" });
        return;
      }
    }
    pushed.current = true;
    liveZone.current = state.zone;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Queue the zone first, then load. jads.js reads the queue as it boots and throws if
    // it is missing, so pushing in the load callback would be too late — see juicyads.ts.
    // The <ins> is already in the DOM here: React commits before it runs effects.
    const late = pushZone(state.zone);
    // A fill is an <iframe> appearing anywhere in the host, NOT a child of the <ins>:
    // jads.js REPLACES the <ins> with the iframe rather than filling it. Checking the
    // <ins> for children therefore reports "empty" even on a perfectly good fill, and
    // the deadline below then tears a live ad off the page.
    const isFilled = () => !!hostRef.current?.querySelector("iframe");
    const settle = (scriptFailed: boolean, deadlinePassed: boolean) => {
      // The first cause wins: it is what the house card's event reports as its reason.
      houseCause.current ??= { scriptFailed, deadlinePassed };
      if (adOutcome({ iframe: isFilled(), scriptFailed, deadlinePassed }) === "house") setState({ kind: "house" });
      else if (isFilled()) report({ iframe: true, scriptFailed, deadlinePassed, houseCard: false });
    };
    // Counted from the push as well as from the load: a script some blocker stalls
    // rather than refuses never settles either way, and would hold the box open forever.
    timer = setTimeout(() => settle(false, true), FILL_TIMEOUT_MS);
    const observer = new MutationObserver(() => {
      if (!isFilled()) return;
      setFilled(true);
      report({ iframe: true, scriptFailed: false, deadlinePassed: false, houseCard: false });
      observer.disconnect();
      clearTimeout(timer);
    });
    if (hostRef.current) observer.observe(hostRef.current, { childList: true, subtree: true });

    loadJuicyAds()
      .then(() => {
        clearTimeout(timer);
        // The script booted before this zone was queued, so it will never read it: place
        // the zone ourselves. The observer above sees the iframe land like any other fill.
        const zone = liveZone.current;
        if (late && zone && hostRef.current) placeLateZone(hostRef.current, zone);
        timer = setTimeout(() => settle(false, true), FILL_TIMEOUT_MS);
      })
      .catch(() => {
        // Blocked or unreachable. The house card rather than an empty box held open.
        clearTimeout(timer);
        pushed.current = false;
        settle(true, false);
      });
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
    // promo only matters on the shop's turn, before anything is pushed; the ref guards repeats.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, promo.ready]);

  useEffect(() => {
    if (state.kind === "house" || state.kind === "shop") onHouse?.();
  }, [state, onHouse]);

  // Reported from the commit that put the card on screen, so house_card means it showed.
  useEffect(() => {
    if (state.kind !== "house") return;
    const cause = houseCause.current ?? { scriptFailed: false, deadlinePassed: true };
    report({ iframe: false, ...cause, houseCard: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- report reads refs only
  }, [state]);

  if (state.kind === "unknown" || state.kind === "off") return null;
  if (state.kind === "house" || state.kind === "shop") {
    return <HouseCard width={size.width} height={size.height} className={className} shopTurn={state.kind === "shop"} fill={fill} />;
  }

  return (
    // `isolate`: the network's iframe arrives as `position: relative; z-index: 292`
    // (measured on prod, 2026-10-05), and with no stacking context around it that 292
    // competed with the whole page — the Browse rail ad painted OVER the clip viewer
    // (z-60) and its Previous arrow. Isolated, the creative's z-index only orders it
    // inside this box, and every modal sits above the ad as it should.
    <aside
      className={`isolate flex flex-col items-center justify-start ${className ?? ""}`}
      aria-label="Advertisement"
    >
      {/* Labelled only once something is actually in the box — a caption over an empty
          rectangle reads as broken. The element is always rendered, just invisible, so
          the label appearing does not nudge the page. */}
      <span
        className="mb-1 text-[10px] uppercase tracking-wide text-zinc-600"
        style={{ visibility: state.kind === "live" && !filled ? "hidden" : "visible" }}
      >
        Advertisement
      </span>
      {state.kind === "placeholder" ? (
        <div
          className="rounded border border-[var(--color-edge)] bg-[var(--color-panel-2)]"
          style={{ width: size.width, height: size.height, maxWidth: "100%" }}
        />
      ) : (
        // Third-party territory. The <ins> is written once via dangerouslySetInnerHTML so
        // React treats this subtree as opaque and never reconciles it — jads.js REPLACES
        // that <ins> with an iframe, and a React that still believed it owned the node
        // would be diffing against an element that no longer exists.
        //
        // The host carries the reserved size so the box holds its space before the
        // creative lands, and clips it: the creative is a fixed-size iframe the network
        // controls, and on a 320px phone the column is narrower than a 300px unit, which
        // would otherwise widen the document and scroll the whole page sideways.
        <div
          ref={hostRef}
          style={{
            width: size.width,
            height: size.height,
            maxWidth: "100%",
            overflow: "hidden",
          }}
          dangerouslySetInnerHTML={{ __html: insMarkup(state.zone, size) }}
        />
      )}
    </aside>
  );
}

/**
 * Our own card where the network's unit never arrived. Labelled as what it is, not as an
 * "Advertisement", and with no ad-ish names on it: a blocker's cosmetic filters would
 * otherwise hide the card too. Sized to its content and capped at the unit's width, so
 * on a phone it is one short row instead of the unit's tall box.
 *
 * Two layouts (lib/adHouseCard houseCardLayout): a narrow unit (the 160px sidebar
 * skyscraper) gets the COMPACT stack — title on one line, one short sentence, the button
 * below it, full width. Everything else gets the ROW, which WRAPS: the button is in
 * normal flow and drops under the text whenever the row runs out of room, so at no width
 * can it sit on top of the copy.
 */
/** From this height up, a shop card fills the unit as a panel (the 300x100 footer stays a row). */
const PANEL_MIN_HEIGHT = 250;

function HouseCard({
  width,
  height,
  className,
  shopTurn = false,
  member = false,
  fill = false,
}: {
  width: number;
  /** The unit's height. A tall unit (a 250px rectangle, the 600px skyscraper) gets the shop
   *  as a full panel the size of the ad it stands in for, not a strip floating in its box. */
  height: number;
  className?: string;
  shopTurn?: boolean;
  /** A Premium member's slot: always the shop, and never the Premium pitch. */
  member?: boolean;
  /** Fill the parent cell rather than the unit's own size. */
  fill?: boolean;
}) {
  const { sellingEnabled } = useGoldGate();
  const [goldOpen, setGoldOpen] = useState(false);
  // The shop's answer decides between three cards, so wait for it rather than flip a card
  // that is already showing. It is one cached request, shared with the menu's Shop row.
  const { ready, product: promo } = useShopPromo();
  const [coin] = useState(() => Math.random());
  const kind = shopTurn && promo ? "shop" : houseCardKind(sellingEnabled, !!promo, coin);
  const layout = houseCardLayout(width);
  const compact = layout === "compact";
  const goPremium = (
    <button
      type="button"
      onClick={() => setGoldOpen(true)}
      className={`shrink-0 rounded-lg bg-[#f5c518] py-1.5 font-semibold text-black hover:brightness-110 ${compact ? "w-full px-2 text-[13px] leading-tight" : "px-3 text-sm whitespace-nowrap"}`}
    >
      Go Premium
    </button>
  );
  const howLink = (
    <Link
      to={HOW_TO_LINK.to}
      className={`shrink-0 rounded-lg border border-[var(--color-edge)] py-1.5 text-center font-medium text-zinc-100 hover:bg-white/10 ${compact ? "w-full px-2 text-[13px] leading-tight" : "px-3 text-sm whitespace-nowrap"}`}
    >
      {HOW_TO_LINK.label}
    </Link>
  );
  if (!ready) return null;
  const reason = member ? "member" : shopTurn ? "rotation" : "ad_fallback";
  // A tall unit gets the shop as a panel the size of the ad, so nothing floats in the box.
  if (kind === "shop" && promo && height >= PANEL_MIN_HEIGHT) {
    return (
      // fill: `contents`, so the card can pin itself to the CELL's edges and never set the
      // row's height; the tiles beside it do.
      <div className={`${fill ? "contents" : "flex justify-center"} ${className ?? ""}`} data-house-card={kind} data-house-layout={fill ? "fill" : "panel"}>
        <ShopPromoCard product={promo} compact={compact} reason={reason} panel={{ width, height }} fill={fill} />
      </div>
    );
  }
  return (
    <div className={`flex justify-center ${className ?? ""}`} data-house-card={kind} data-house-layout={layout}>
      {compact ? (
        <div
          className="flex w-full flex-col items-center gap-2 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)]/60 p-2.5 text-center"
          style={{ maxWidth: width }}
        >
          {kind === "shop" && promo ? (
            <ShopPromoCard product={promo} compact reason={reason} />
          ) : kind === "premium" ? (
            <>
              <p className="flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap text-zinc-100">
                <GoldChip /> {NO_ADS_PERK.title}
              </p>
              <p className="text-xs text-zinc-400">{COMPACT_NO_ADS_SUB}</p>
              {goPremium}
            </>
          ) : (
            <>
              <p className="text-xs text-zinc-300">New here? Every screen, explained.</p>
              {howLink}
            </>
          )}
        </div>
      ) : (
        <div
          className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)]/60 p-3 text-left"
          style={{ maxWidth: width }}
        >
          {kind === "shop" && promo ? (
            <ShopPromoCard product={promo} compact={false} reason={reason} />
          ) : kind === "premium" ? (
            <>
              <div className="min-w-[8rem] flex-1">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-zinc-100">
                  <GoldChip /> {NO_ADS_PERK.title}
                </p>
                <p className="mt-0.5 text-xs text-zinc-400">{NO_ADS_PERK.sub}</p>
              </div>
              {goPremium}
            </>
          ) : (
            <>
              <p className="min-w-[8rem] flex-1 text-sm text-zinc-300">New here? Every screen, wall and Feed, explained.</p>
              {howLink}
            </>
          )}
        </div>
      )}
      {goldOpen ? <GoldDialog source="menu" onClose={() => setGoldOpen(false)} /> : null}
    </div>
  );
}
